import ExpandMoreIcon from "@mui/icons-material/ExpandMore"
import StorefrontIcon from "@mui/icons-material/Storefront"
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Chip,
  Collapse,
  Typography
} from "@mui/material"
import { AiMessageSteps } from "__generated__/graphql"
import { useMemo, useState } from "react"
import {
  buildCommerceRun,
  CommerceProductCard,
  CommerceRound,
  CommerceRun,
  CommerceToolCall
} from "./commerceRunTimeline.utils"

const seconds = (ms: number) => `${(ms / 1000).toFixed(2)} s`

const WORKFLOW_LABELS: Record<string, string> = {
  PRODUCT_DISCOVERY: "Ürün arama / bilgi",
  ORDER_CREATE: "Sipariş oluşturma",
  ORDER_CANCEL: "Sipariş iptali"
}

/**
 * The cards that left with the answer as their own message. The answer text alone says
 * "the first product", so without these the log cannot be read back.
 */
function ProductCards({ cards }: { cards: CommerceProductCard[] }) {
  if (!cards.length) return null

  return (
    <Box sx={{ mb: 1 }}>
      <Typography
        variant="caption"
        sx={{ display: "block", fontWeight: 700, mb: 0.5 }}
      >
        Gönderilen ürün kartları ({cards.length})
      </Typography>
      <Box sx={{ display: "flex", gap: 1, overflowX: "auto", pb: 0.5 }}>
        {cards.map((card, index) => (
          <Box
            key={card.productId ?? `${card.title ?? "card"}-${index}`}
            sx={{
              width: 150,
              minWidth: 150,
              p: 1,
              border: "1px solid #e0e0e0",
              borderRadius: 1,
              bgcolor: "#fff"
            }}
          >
            {card.thumbnailUrl && (
              <Box
                component="img"
                src={card.thumbnailUrl}
                alt={card.title ?? ""}
                sx={{
                  width: "100%",
                  height: 120,
                  objectFit: "cover",
                  borderRadius: 1,
                  mb: 0.5
                }}
              />
            )}
            <Typography
              variant="caption"
              sx={{
                display: "block",
                fontWeight: 700,
                overflowWrap: "anywhere"
              }}
            >
              {index + 1}) {card.title ?? card.productId ?? "-"}
            </Typography>
            {card.optionsSummary && (
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ display: "block" }}
              >
                {card.optionsSummary}
              </Typography>
            )}
            {card.price && (
              <Typography variant="caption" sx={{ display: "block" }}>
                {card.price}
              </Typography>
            )}
            {card.productUrl && (
              <Typography
                component="a"
                href={card.productUrl}
                target="_blank"
                rel="noreferrer"
                variant="caption"
                sx={{ display: "block", overflowWrap: "anywhere" }}
              >
                {card.productUrl}
              </Typography>
            )}
          </Box>
        ))}
      </Box>
    </Box>
  )
}

function ToolCall({ call }: { call: CommerceToolCall }) {
  return (
    <Box
      sx={{
        mt: 0.75,
        p: 1,
        borderRadius: 1,
        minWidth: 0,
        bgcolor: call.failed ? "rgba(211, 47, 47, 0.05)" : "action.hover"
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 0.75,
          flexWrap: "wrap"
        }}
      >
        <Chip
          label={call.name}
          size="small"
          color={call.failed ? "error" : "primary"}
          sx={{ height: 20, fontSize: 11 }}
        />
        <Typography variant="caption" color="text.secondary">
          {seconds(call.durationMs)}
          {call.resultCount !== undefined ? ` · ${call.resultCount} kayıt` : ""}
        </Typography>
      </Box>

      {call.args.length > 0 && (
        <Box sx={{ mt: 0.5, display: "flex", flexWrap: "wrap", gap: 0.5 }}>
          {call.args.map(([key, value]) => (
            <Chip
              key={key}
              size="small"
              variant="outlined"
              label={`${key}: ${value.length > 40 ? `${value.slice(0, 40)}…` : value}`}
              sx={{ height: 18, fontSize: 10, maxWidth: "100%" }}
            />
          ))}
        </Box>
      )}

      {call.records.length > 0 && (
        <Box sx={{ mt: 0.75 }}>
          {call.records.slice(0, 5).map((record) => (
            <Typography
              key={`${record.title}-${record.url ?? ""}`}
              variant="caption"
              sx={{ display: "block", overflowWrap: "anywhere" }}
            >
              • {record.title}
              {record.price ? ` · ${record.price}` : ""}
              {record.availability ? ` · ${record.availability}` : ""}
            </Typography>
          ))}
          {call.records.length > 5 && (
            <Typography variant="caption" color="text.secondary">
              +{call.records.length - 5} kayıt daha
            </Typography>
          )}
        </Box>
      )}

      {call.note && (
        <Typography
          variant="caption"
          sx={{ display: "block", mt: 0.5, fontStyle: "italic" }}
        >
          “{call.note}”
        </Typography>
      )}

      {call.result !== undefined && call.result !== null && (
        <Accordion
          disableGutters
          elevation={0}
          sx={{ bgcolor: "transparent", "&::before": { display: "none" } }}
        >
          <AccordionSummary
            expandIcon={<ExpandMoreIcon sx={{ fontSize: 16 }} />}
            sx={{
              minHeight: 0,
              p: 0,
              "& .MuiAccordionSummary-content": { my: 0.5 }
            }}
          >
            <Typography variant="caption" color="primary">
              Tam sonuç
            </Typography>
          </AccordionSummary>
          <AccordionDetails sx={{ p: 0 }}>
            <Box
              component="pre"
              sx={{
                m: 0,
                p: 1,
                fontSize: 11,
                maxHeight: 240,
                overflowY: "auto",
                overflowWrap: "anywhere",
                whiteSpace: "pre-wrap",
                bgcolor: "grey.100",
                borderRadius: 1
              }}
            >
              {JSON.stringify(call.result, null, 2)}
            </Box>
          </AccordionDetails>
        </Accordion>
      )}
    </Box>
  )
}

function Round({ round }: { round: CommerceRound }) {
  return (
    <Box sx={{ display: "flex", gap: 1, mb: 1.25, minWidth: 0 }}>
      <Box
        sx={{
          width: 22,
          height: 22,
          flexShrink: 0,
          borderRadius: "50%",
          bgcolor: "grey.500",
          color: "common.white",
          fontSize: 12,
          display: "flex",
          alignItems: "center",
          justifyContent: "center"
        }}
      >
        {round.index}
      </Box>
      <Box sx={{ minWidth: 0, flex: 1 }}>
        <Typography
          variant="body2"
          fontWeight={600}
          sx={{ overflowWrap: "anywhere" }}
        >
          {round.summary ?? `Tur ${round.index}`}
        </Typography>
        {round.note && (
          <Typography
            variant="caption"
            sx={{
              display: "block",
              fontStyle: "italic",
              color: "text.secondary"
            }}
          >
            “{round.note}”
          </Typography>
        )}
        {round.toolCalls.map((call, index) => (
          <ToolCall key={`${call.name}-${index}`} call={call} />
        ))}
      </Box>
    </Box>
  )
}

/**
 * The commerce (MCP) view of a run: the shopper request as the model classified it, the tools it
 * was allowed to use, every round of tool calls with their arguments and what came back, and how
 * the workflow ended. It sits next to the Skill timeline — one message can run both.
 */

/**
 * Three distinct outcomes, not two: a listing can be identified and still be unbuyable, which is
 * the case the resolver reports as MATCHED_NOT_SELLABLE — collapsing it into "bulundu" hides the
 * single most useful fact about the run.
 */
const marketplaceListingLabel = (
  listing: NonNullable<CommerceRun["marketplaceProduct"]>
): string => {
  if (!listing.matched) return "katalogda yok"
  return listing.sellable === false
    ? "katalogda bulundu — satışta değil"
    : "katalogda bulundu"
}

/** Which source actually supplied the product text — the whole point of the two-step ordering. */
const DETAIL_SOURCE_LABELS: Record<string, string> = {
  CATALOG: "ürün bilgisi Qpien Shop kataloğundan",
  MARKETPLACE_API: "ürün bilgisi canlı pazaryeri API'sinden (fallback)"
}

export default function CommerceRunTimeline({
  steps
}: {
  steps: AiMessageSteps[] | undefined
}) {
  const run = useMemo(() => buildCommerceRun(steps), [steps])
  const [open, setOpen] = useState(true)

  if (!run) return null

  return (
    <Box sx={{ px: 2, pb: 1.5, minWidth: 0, overflowX: "hidden" }}>
      <Box
        sx={{
          border: 1,
          borderColor: "divider",
          borderRadius: 2,
          p: 1.5,
          minWidth: 0,
          bgcolor: "background.paper"
        }}
      >
        <Box
          onClick={() => setOpen(!open)}
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            minWidth: 0,
            cursor: "pointer",
            borderRadius: 1,
            "&:hover": { bgcolor: "action.hover" }
          }}
        >
          <StorefrontIcon
            sx={{ fontSize: 18, color: "primary.main", flexShrink: 0 }}
          />
          <Typography
            variant="body2"
            fontWeight={700}
            noWrap
            sx={{ flex: 1, minWidth: 0 }}
          >
            Mağaza akışı (MCP)
          </Typography>
          <Chip
            label={run.outcome.label}
            size="small"
            color={
              run.outcome.tone === "default" ? undefined : run.outcome.tone
            }
            sx={{ height: 22, flexShrink: 0 }}
          />
          <ExpandMoreIcon
            sx={{
              fontSize: 20,
              color: "text.disabled",
              flexShrink: 0,
              transform: open ? "none" : "rotate(-90deg)",
              transition: "transform 120ms"
            }}
          />
        </Box>

        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ display: "block", mt: 0.25, mb: 1 }}
        >
          {run.rounds.length} tur ·{" "}
          {run.rounds.reduce(
            (total, round) => total + round.toolCalls.length,
            0
          )}{" "}
          araç çağrısı · {seconds(run.durationMs)}
        </Typography>

        <Collapse in={open} timeout={120} unmountOnExit>
          {(run.workflowType || run.productQuery) && (
            <Box sx={{ mb: 1 }}>
              <Typography variant="caption" color="text.secondary">
                Müşterinin isteği
                {run.workflowType
                  ? ` · ${WORKFLOW_LABELS[run.workflowType] ?? run.workflowType}`
                  : ""}
                {run.selection ? ` · ${run.selection}` : ""}
              </Typography>
              {run.productQuery && (
                <Typography
                  variant="body2"
                  sx={{ fontStyle: "italic", overflowWrap: "anywhere" }}
                >
                  “{run.productQuery}”
                </Typography>
              )}
            </Box>
          )}

          {run.pageProduct && (
            <Typography
              variant="caption"
              sx={{ display: "block", mb: 1, overflowWrap: "anywhere" }}
            >
              Sayfa ürünü: {run.pageProduct.matched ? "eşleşti" : "eşleşmedi"}
              {run.pageProduct.reason ? ` (${run.pageProduct.reason})` : ""}
              {run.pageProduct.url ? ` — ${run.pageProduct.url}` : ""}
            </Typography>
          )}

          {run.marketplaceProduct && (
            <Typography
              variant="caption"
              sx={{ display: "block", mb: 1, overflowWrap: "anywhere" }}
            >
              Pazaryeri ürünü: {marketplaceListingLabel(run.marketplaceProduct)}
              {run.marketplaceProduct.reason
                ? ` (${run.marketplaceProduct.reason})`
                : ""}
              {run.marketplaceProduct.withdrawn ? " — ilan kaldırılmış" : ""}
              {run.marketplaceProduct.platform
                ? ` — ${run.marketplaceProduct.platform}`
                : ""}
              {run.marketplaceProduct.ref
                ? ` — ${run.marketplaceProduct.ref}`
                : ""}
              {run.marketplaceProduct.shopId
                ? ` — mağaza ${run.marketplaceProduct.shopId}`
                : ""}
              {run.marketplaceProduct.detailSource
                ? ` — ${
                    DETAIL_SOURCE_LABELS[run.marketplaceProduct.detailSource] ??
                    run.marketplaceProduct.detailSource
                  }`
                : ""}
            </Typography>
          )}

          {run.tools && (
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ display: "block", mb: 1, overflowWrap: "anywhere" }}
            >
              {run.tools.count} araç hazırlandı
              {run.tools.step ? ` (${run.tools.step})` : ""}
              {run.tools.names.length ? `: ${run.tools.names.join(", ")}` : ""}
            </Typography>
          )}

          <ProductCards cards={run.productCards} />

          {run.rounds.map((round) => (
            <Round key={round.index} round={round} />
          ))}

          {run.errors.map((error) => (
            <Typography
              key={error}
              variant="caption"
              sx={{
                display: "block",
                color: "error.main",
                overflowWrap: "anywhere"
              }}
            >
              {error}
            </Typography>
          ))}
        </Collapse>
      </Box>
    </Box>
  )
}
